import { createFileRoute } from '@tanstack/react-router'
import thai2englishApi from 'api/thai2english-api'
import { buildThaiWordResult } from 'lib/processQuery'
import { canonicalThaiEntry } from 'lib/dictionary-routes'
import {
   canonicalLink,
   definedTermSchema,
   dictionaryDescription,
   dictionaryUrl,
   ldJsonScript,
   pageMeta,
} from 'lib/seo'
import { entryCacheControl } from 'lib/entry-cache'
import { getTranslitFromPhonetic } from 'lib/transliteration'
import { t2eTlitScheme } from 'nlp'
import defaultUserPrefs from 'lib/defaultUserPrefs'
import ThaiWordPage from 'pages/thai-word'

// The meta description's romanization. `t2e` is the Thai-script phonetic
// respelling, not display text; render it with the site's own scheme — the head
// is built on the server, where there are no user prefs to read, and a crawler
// should always be given a romanization even though a reader can turn theirs
// off.
const defaultTranslit = (t2e: string): string =>
   getTranslitFromPhonetic(
      t2e,
      { addToneMarkers: defaultUserPrefs.toneMarkers === 'Yes' },
      t2eTlitScheme
   )

export const Route = createFileRoute('/thai/$word')({
   // Runs on the server for a fresh document request and on the client for an
   // in-app navigation, so the entry is in the first byte of HTML a crawler
   // sees and costs no extra round trip once the app has hydrated.
   loader: async ({ params }) => {
      const { word } = params
      const searchResults = await thai2englishApi.searchDictionary(word)
      return buildThaiWordResult(canonicalThaiEntry(word, searchResults), searchResults)
   },
   head: ({ loaderData }) => {
      // Undefined while the loader is still running, and on the error path.
      if (!loaderData) return {}

      const { word, t2e, meanings } = loaderData.dictionaryWord
      const url = dictionaryUrl('thai', word)
      const description = dictionaryDescription(
         word,
         defaultTranslit(t2e),
         meanings.map((m) => m.meaning)
      )

      return {
         meta: pageMeta({
            title: `${word} - Thai / English dictionary meaning - ภาษาอังกฤษ แปล ความหมาย`,
            description,
            url,
            ogType: 'article',
         }),
         links: [canonicalLink(url)],
         scripts: [ldJsonScript(definedTermSchema({ url, word, description }))],
      }
   },
   headers: () => entryCacheControl,
   component: ThaiWordPage,
})
